import {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import axiosManager from "../../../../../services/axiosManager";
import { useStudents } from "../../../../../context/students";
import Preloader from "../../../../../components/Preloader";
import subjectSelectionService from "../../../../../services/subjectSelectionService";
import SubjectButton from "./SubjectsButton";
import Dialog from "../../../../../components/Dialog";
import { useTranslate } from "../../../../../hooks/useTranslate";
import IconAlert from "../../../../../assets/images/icons/alert.svg?react";
import IconInfo from "../../../../../assets/images/icons/info.svg?react";
import useStatuses from "../../../../../context/status/useStatuses";
import "./SubjectsSelection.css";

const STEP_TIME_ZONE = "time-zone";
const STEP_YEAR_GROUP = "year-group";
const STEP_PACKAGE = "package";
const STEP_SUBJECTS = "subjects";
const STEP_HIGHER_SUBJECTS = "higher-subjects";
const STEPS = [STEP_TIME_ZONE, STEP_YEAR_GROUP, STEP_PACKAGE, STEP_SUBJECTS, STEP_HIGHER_SUBJECTS];

const SubjectsSelection = (props) => {
    const { onClose } = props;
    const { t } = useTranslate();
    const { activeStudentId, activeStudent } = useStudents();
    const { refetchGeneralStatuses } = useStatuses();

    const [step, setStep] = useState(STEPS[0]);
    const [divisionSelected, setDivisionSelected] = useState(null);
    const [yearGroupSelected, setYearGroupSelected] = useState(null);
    const [packageSelected, setPackageSelected] = useState(null);
    const [subjectsSelected, setSubjectsSelected] = useState([]);

    const [showCheckModal, setShowCheckModal] = useState(false);

    const {
        isFetching: isLoadingDivisions, data: divisions,
    } = useQuery({
        queryKey: [`students/${activeStudentId}/divisions`],
        queryFn: () => axiosManager.get$("parent/api/divisions").then((response) => response?.data),
        retry: 0,
    });
    const {
        data: yearGroups, mutateAsync: getYearGroups, isPending: isLoadingYearGroups,
    } = useMutation({
        mutationFn: () => axiosManager.get$(`/parent/api/division/${divisionSelected}/yearGroups`).then((response) => response?.data?.sets),
    });
    const {
        data: product, mutateAsync: getProduct, isPending: isLoadingProduct,
    } = useMutation({
        mutationFn: () => axiosManager.get$(`/parent/api/yearGroups/${yearGroupSelected}/packages?startDate=${activeStudent?.desiredStartDate}`).then((response) => response?.data?.packages),
    });

    const packageProduct = (product || []).find((item) => item.productId === packageSelected);

    const {
        mutateAsync: saveSubjects, reset: resetSaveSubjects,
        isPending: isSavingSubjects, isSuccess: isSuccessSaveSubjects, error: errorSaveSubjects,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/subjects`, {
            divisionId: divisionSelected,
            yearGroupId: yearGroupSelected,
            package: {
                name: packageProduct?.name,
                productId: packageSelected,
            },
            subjects: subjectSelectionService.getSelectedSubjects(packageProduct, subjectsSelected).map((s) => ({
                name: s.name,
                productId: s.productId,
                category: s.sectionName,
            })),
        }),
        onSuccess: () => {
            onClose();
            refetchGeneralStatuses();
        },
    });

    const subjectSections = useMemo(() => {
        if (!packageSelected) return [];
        return subjectSelectionService.getSections(packageProduct);
    }, [packageProduct, packageSelected]);
    const areRequiredSelected = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.checkIfRequiredSelected(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);
    const hasHigherSubjects = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.hasHigherSubjects(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);
    const higherSubjectsToSelect = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.getHigherSubjectsToSelect(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);

    const onPrev = () => {
        const currentIdx = STEPS.indexOf(step);
        if (currentIdx !== 0) setStep(STEPS[currentIdx - 1]);
        else onClose();
    };
    const onNext = () => {
        const currentIdx = STEPS.indexOf(step);
        if (step === STEP_TIME_ZONE) {
            getYearGroups();
        }
        if (step === STEP_YEAR_GROUP) {
            getProduct();
        }
        if ((step === STEP_SUBJECTS && !hasHigherSubjects) || step === STEP_HIGHER_SUBJECTS) {
            saveSubjects();
            return;
        }

        setStep(STEPS[currentIdx + 1]);
    };

    const modalCheck = useMemo(() => {
        if (!packageSelected) return null;
        const {
            hasAbsoluteMaxSubjects,
            hasIncludedMaxSubjects,
            hasRecommendedMaxSubjects,
        } = subjectSelectionService.checkLimits(packageProduct, subjectsSelected);
        const { includedMaxSubjects, recommendedMaxSubjects } = packageProduct || {};
        if (hasAbsoluteMaxSubjects) {
            return {
                title: t("subjects.absoluteMaxSubjects.title"),
                description: t("subjects.absoluteMaxSubjects.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.absoluteMaxSubjects.cancel"),
                confirmationText: t("subjects.absoluteMaxSubjects.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNext,
                confirmDisabled: !areRequiredSelected,
            };
        }
        if (hasIncludedMaxSubjects) {
            return {
                title: t("subjects.includedMaxSubjectsModal.title"),
                description: t("subjects.includedMaxSubjectsModal.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.includedMaxSubjectsModal.cancel"),
                confirmationText: t("subjects.includedMaxSubjectsModal.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNext,
                confirmDisabled: !areRequiredSelected,
            };
        }

        if (hasRecommendedMaxSubjects) {
            return {
                title: t("subjects.recommendedMaxSubjectsModal.title", { num: recommendedMaxSubjects }),
                description: t("subjects.recommendedMaxSubjectsModal.subtitle"),
                dialogType: "error",
                confirmationText: t("subjects.recommendedMaxSubjectsModal.confirm"),
                onConfirm: () => setShowCheckModal(false),
            };
        }

        return null;
    }, [packageSelected, subjectsSelected, t, onNext]);

    useEffect(() => {
        if (modalCheck) setShowCheckModal(true);
    }, [!!modalCheck]);

    const onPackageClick = (packageId) => {
        setPackageSelected(packageId);

        const packageToSet = (product || []).find((item) => item.productId === packageId);
        const lockedSubjects = packageToSet ? subjectSelectionService.getLockedSubjects(packageToSet) : [];
        setSubjectsSelected(lockedSubjects.map((x) => x.productId));
    };
    const handleSubjectSelection = (subjectId) => {
        if (subjectsSelected.includes(subjectId)) {
            setSubjectsSelected((prev) => prev.filter((item) => item !== subjectId));
        } else {
            setSubjectsSelected((prev) => [...prev, subjectId]);
        }
    };
    const onSubjectClick = (subject) => {
        const isSelectedHigher = subject.higherSubject && subjectsSelected.includes(subject.higherSubject.productId);

        if (isSelectedHigher && subject.higherSubject) {
            handleSubjectSelection(subject.higherSubject.productId);
        } else {
            handleSubjectSelection(subject.productId);
        }
    };
    const onHigherSubjectClick = (subject, isHigher = false) => () => {
        const { higherSubject } = subject;

        if (isHigher) {
            setSubjectsSelected((prev) => [...prev.filter((item) => item !== subject.productId), higherSubject.productId]);
        } else {
            setSubjectsSelected((prev) => [...prev.filter((item) => item !== higherSubject.productId), subject.productId]);
        }
    };

    const convertTimeAMPM = useCallback((time) => {
        if (!time) return "";

        const ampm = +time.split(":")[0] >= 12 ? "pm" : "am";
        return time.replace(/.{3}$/, ampm);
    }, []);

    return (
        <div className="subject-selection">
            <div className="divider mt-4 mb-4" />
            {step === STEP_TIME_ZONE && (
                <div>
                    <Typography variant="h5" component="div">
                        <b className="font-bold">{t("subjectSelection.timeZone.title")}</b>
                        <br />
                        {t("subjectSelection.timeZone.subtitle")}
                    </Typography>
                    <Preloader isLoading={isLoadingDivisions} size={40}>
                        <div className="flex flex-wrap align-stretch gap-8 mt-4">
                            {(divisions || []).map((division) => (
                                <button
                                    key={division.divisionId}
                                    type="button"
                                    className={`selection-option selection-option--division ${division.divisionId === divisionSelected ? "selected" : ""}`}
                                    onClick={() => setDivisionSelected(division.divisionId)}
                                >
                                    {division.name}
                                    <span>{`Lessons ${division.weekStartName} - ${division.weekEndName}`}</span>
                                    <span>
                                        {`Starting ${convertTimeAMPM(
                                            division.startTime,
                                        )} ${division.timeZoneDisplay}`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </Preloader>
                </div>
            )}
            {step === STEP_YEAR_GROUP && (
                <div>
                    <Typography variant="h5" component="div">
                        <b className="font-bold">{t("subjectSelection.yearGroup.title")}</b>
                        <br />
                        {t("subjectSelection.yearGroup.subtitle")}
                    </Typography>
                    <Preloader isLoading={isLoadingYearGroups} size={40}>
                        {(yearGroups || []).map((yearGroup, idx) => (
                            <div key={yearGroup.name} className="w-full mt-4">
                                <Typography variant="h5" component="div">
                                    <b className="font-bold">{yearGroup.name}</b>
                                </Typography>
                                <div className="flex flex-wrap align-stretch gap-8 mt-4">
                                    {yearGroup.yearGroups.map((year) => (
                                        <button
                                            key={year.yearGroupId}
                                            type="button"
                                            className={`selection-option ${year.yearGroupId === yearGroupSelected ? "selected" : ""}`}
                                            onClick={() => setYearGroupSelected(year.yearGroupId)}
                                        >
                                            {year.name}
                                        </button>
                                    ))}
                                </div>
                                {idx !== yearGroups.length - 1 && <div className="divider mt-4" />}
                            </div>
                        ))}
                    </Preloader>
                </div>
            )}
            {step === STEP_PACKAGE && (
                <div>
                    <Typography variant="h5" component="div">
                        <b className="font-bold">{t("subjectSelection.package.title")}</b>
                        <br />
                        {t("subjectSelection.package.subtitle")}
                    </Typography>
                    <Preloader isLoading={isLoadingProduct} size={40}>
                        <div className="flex flex-wrap align-stretch gap-8 mt-4">
                            {(product || []).map((packageProduct) => (
                                <button
                                    key={packageProduct.productId}
                                    type="button"
                                    className={`selection-option selection-option--division ${packageProduct.productId === packageSelected ? "selected" : ""}`}
                                    onClick={() => onPackageClick(packageProduct.productId)}
                                >
                                    {packageProduct.name}
                                </button>
                            ))}
                        </div>
                    </Preloader>
                </div>
            )}
            {step === STEP_SUBJECTS && (
                <div>
                    {(subjectSections || []).map((section, idx) => (
                        <div key={section.productId} className="w-full mt-4">
                            <Typography variant="h5" component="div">
                                <b className="font-bold">{section.name}</b>
                            </Typography>
                            <Typography variant="body1" component="div">{section.instructions}</Typography>
                            <div className="flex flex-wrap align-stretch gap-4 mt-4">
                                {(section.subjects || []).map((subject) => (
                                    <SubjectButton
                                        key={subject.productId}
                                        subject={subject}
                                        packageProduct={packageProduct}
                                        subjectsSelected={subjectsSelected}
                                        onSubjectClick={onSubjectClick}
                                    />
                                ))}
                            </div>
                            {idx !== subjectSections.length - 1 && <div className="divider mt-4" />}
                        </div>
                    ))}
                </div>
            )}
            {step === STEP_HIGHER_SUBJECTS && (
                <div>
                    {(higherSubjectsToSelect || []).map((subject, idx) => (
                        <div className="w-full mt-4" key={subject.productId}>
                            <Typography variant="h5" component="div">
                                <b className="font-bold">{subject.name}</b>
                            </Typography>
                            <div className="flex flex-wrap align-stretch gap-4 mt-4">
                                <SubjectButton
                                    key={subject.productId}
                                    subject={subject}
                                    packageProduct={packageProduct}
                                    subjectsSelected={subjectsSelected}
                                    isHigherSelection
                                    onSubjectClick={onHigherSubjectClick(subject)}
                                />
                                <SubjectButton
                                    key={subject.higherSubject.productId}
                                    subject={subject.higherSubject}
                                    packageProduct={packageProduct}
                                    subjectsSelected={subjectsSelected}
                                    isHigherSelection
                                    onSubjectClick={onHigherSubjectClick(subject, true)}
                                />
                            </div>
                            {idx !== higherSubjectsToSelect.length - 1 && <div className="divider mt-4" />}
                        </div>
                    ))}
                </div>
            )}
            <div className="divider mt-8 mb-8" />
            <div className="flex align-center justify-between gap-4 w-full">
                <Button
                    variant="outlined"
                    className="subject-selection__button"
                    disabled={isSavingSubjects || isLoadingDivisions || isLoadingYearGroups || isLoadingProduct}
                    onClick={onPrev}
                >
                    {step === STEP_TIME_ZONE ? t("button.cancel") : t("button.prev")}
                </Button>
                <Button
                    variant="contained"
                    className="button-accent subject-selection__button"
                    disabled={isLoadingDivisions || isLoadingYearGroups || isLoadingProduct || (step === STEP_SUBJECTS && !areRequiredSelected)}
                    onClick={onNext}
                    loading={isSavingSubjects}
                >
                    {step === STEP_HIGHER_SUBJECTS && t("button.save")}
                    {step === STEP_SUBJECTS ? (hasHigherSubjects ? t("button.next") : t("button.save")) : null}
                    {![STEP_SUBJECTS, STEP_HIGHER_SUBJECTS].includes(step) && t("button.next")}
                </Button>
            </div>

            {modalCheck && (
                <Dialog
                    open={!!showCheckModal}
                    onClose={() => setShowCheckModal(false)}
                    className={`subject-dialog ${modalCheck.dialogType === "error" ? "subject-dialog--error" : ""}`}
                >
                    {modalCheck.dialogType === "error"
                        ? <IconAlert className="subject-dialog__icon" />
                        : <IconInfo className="subject-dialog__icon" />}
                    <Typography variant="h5" component="h3" className="subject-dialog__title">
                        {modalCheck.title}
                    </Typography>
                    <Typography variant="body1" component="div" className="subject-dialog__description">
                        {modalCheck.description}
                    </Typography>
                    <div className="subject-dialog__footer">
                        {modalCheck.cancellationText && (
                            <Button
                                className="button-accent subject-dialog__btn"
                                onClick={modalCheck.onCancel}
                            >
                                {modalCheck.cancellationText}
                            </Button>
                        )}
                        {modalCheck.confirmationText && (
                            <Button
                                className="button-accent subject-dialog__btn"
                                onClick={modalCheck.onConfirm}
                                disabled={modalCheck.confirmDisabled}
                            >
                                {modalCheck.confirmationText}
                            </Button>
                        )}
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default SubjectsSelection;
